/**
 * The proposal builder: a large popup over the job card, or over Settings.
 *
 * **On a job** it writes a draft quote. It opens on a choice — one of the
 * business's templates, a blank proposal, or just a price list (the old
 * `RaiseDocumentPopup`) — and from there every edit is saved as it is made,
 * debounced, through `quotes.update`. There is no Save button because there
 * is nothing to lose: a draft is not the customer's until it is sent, and
 * sending is on the billing tab, a separate step (decision 72).
 *
 * **In Settings** it writes a template, and there it *does* have a Save
 * button: a template is shared by everybody raising proposals, so half an
 * edit should not reach them.
 *
 * The draft is seeded once from the server and never re-seeded on a refetch,
 * the same rule as the raise popup: the office is typing into it.
 * Decision 80.
 */
import { formatJobNumber } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { addressLine, money, relativeTime } from '../../lib/format.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { Button, ErrorState, Field, Input, Select, SkeletonRows, Textarea } from '../ui.js'
import {
  asProposal,
  blankLine,
  draftAsView,
  draftFromQuote,
  draftFromTemplate,
  draftTotals,
  quoteInput,
  starterDraft,
  templateContent,
  type ProposalDraft,
  type ProposalMeta,
} from './draft.js'
import { type LetterheadBusiness } from './Letterhead.js'
import { ProposalCanvas } from './ProposalCanvas.js'
import { ProposalPage, type Choice } from './ProposalPage.js'
import type { DraftUpdate } from './useDraftHistory.js'

const AUTOSAVE_MS = 800

export type ProposalBuilderProps =
  | {
      kind: 'quote'
      open: boolean
      onClose: () => void
      job: {
        id: string
        clientId: string
        categoryId: string | null
        client: { name: string }
      }
      /** A draft to carry on with. Absent: start one. */
      quoteId?: string | null
      /** "Just a price list" hands over to the plain raise popup. */
      onQuickQuote?: () => void
    }
  | {
      kind: 'template'
      open: boolean
      onClose: () => void
      /** Absent: a new template. */
      templateId?: string | null
    }

export function ProposalBuilder(props: ProposalBuilderProps) {
  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      labelledBy="proposal-builder-title"
      className="h-[calc(100dvh-2rem)] max-w-[1480px]"
    >
      {props.open ? props.kind === 'quote' ? <QuoteBuilder {...props} /> : <TemplateBuilder {...props} /> : null}
    </Modal>
  )
}

function QuoteBuilder({
  job,
  quoteId: initialQuoteId,
  onClose,
  onQuickQuote,
}: Extract<ProposalBuilderProps, { kind: 'quote' }>) {
  const [quoteId, setQuoteId] = useState<string | null>(initialQuoteId ?? null)

  if (!quoteId) {
    return (
      <StartScreen
        job={job}
        onClose={onClose}
        onStarted={setQuoteId}
        onQuickQuote={onQuickQuote}
      />
    )
  }

  return <QuoteDraftEditor quoteId={quoteId} job={job} onClose={onClose} />
}

/** Where a proposal starts: a template, or a blank page. */
function StartScreen({
  job,
  onClose,
  onStarted,
  onQuickQuote,
}: {
  job: Extract<ProposalBuilderProps, { kind: 'quote' }>['job']
  onClose: () => void
  onStarted: (quoteId: string) => void
  onQuickQuote?: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const templates = useQuery(trpc.proposalTemplates.list.queryOptions())

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const fromTemplate = useMutation(
    trpc.quotes.fromTemplate.mutationOptions({
      onSuccess: (quote) => {
        invalidate()
        onStarted(quote.id)
      },
    }),
  )
  const create = useMutation(
    trpc.quotes.create.mutationOptions({
      onSuccess: (quote) => {
        invalidate()
        onStarted(quote.id)
      },
    }),
  )

  // A blank proposal still starts from what is on the job: the services'
  // default lines, priced for this client, as a suggestion (rule 12).
  const startBlank = async () => {
    const suggestions = await queryClient
      .fetchQuery(trpc.catalog.linesForJob.queryOptions({ jobId: job.id }))
      .catch(() => [])
    const draft = starterDraft()
    const input = quoteInput({
      ...draft,
      lines: suggestions.flatMap((service) =>
        service.lines.map((line) =>
          blankLine(
            { taxRateId: line.taxRateId, taxRateBasisPoints: 0 },
            {
              description: line.description,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              unitPriceIncTax: line.unitPriceIncTax,
              materialId: line.materialId,
              serviceTypeId: service.serviceTypeId,
            },
          ),
        ),
      ),
    })
    create.mutate({ jobId: job.id, ...input, termsText: undefined })
  }

  const ordered = useMemo(() => {
    const rows = templates.data ?? []
    // The job's own kind of work first; a template for any job next.
    const rank = (row: (typeof rows)[number]) =>
      row.jobCategory?.id && row.jobCategory.id === job.categoryId ? 0 : row.jobCategory ? 2 : 1
    return [...rows].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
  }, [templates.data, job.categoryId])

  const busy = fromTemplate.isPending || create.isPending
  const failure = fromTemplate.error ?? create.error

  return (
    <>
      <BuilderHeader title="New proposal" subtitle={job.client.name} onClose={onClose} />
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <div className="mx-auto flex max-w-3xl flex-col gap-5">
          {failure ? <ErrorState message={errorMessage(failure)} /> : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <StartCard
              title="Blank proposal"
              description="A cover, a page of words and the price. The job's services are added as lines."
              onClick={() => void startBlank()}
              disabled={busy}
            />
            {onQuickQuote ? (
              <StartCard
                title="Just a price list"
                description="Lines and a total, no sections or options — the quick quote."
                onClick={onQuickQuote}
                disabled={busy}
              />
            ) : null}
          </div>

          <section aria-labelledby="proposal-templates-heading" className="flex flex-col gap-2">
            <h3 id="proposal-templates-heading" className="text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase">
              From a template
            </h3>
            {templates.isPending ? (
              <SkeletonRows rows={3} />
            ) : templates.error ? (
              <ErrorState message={errorMessage(templates.error)} onRetry={() => void templates.refetch()} />
            ) : ordered.length === 0 ? (
              <p className="text-sm text-ink-500">
                No templates yet. Save a finished proposal as one, or build them under Settings → Proposals.
              </p>
            ) : (
              <ul className="grid gap-2 sm:grid-cols-2">
                {ordered.map((template) => (
                  <li key={template.id}>
                    <StartCard
                      title={template.name}
                      description={
                        template.description ||
                        [
                          template.optionNames.length > 0 ? template.optionNames.join(' · ') : null,
                          `${template.lineCount} line${template.lineCount === 1 ? '' : 's'}`,
                        ]
                          .filter(Boolean)
                          .join(' — ')
                      }
                      tag={template.jobCategory?.name}
                      onClick={() => fromTemplate.mutate({ jobId: job.id, templateId: template.id })}
                      disabled={busy}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  )
}

function StartCard({
  title,
  description,
  tag,
  onClick,
  disabled,
}: {
  title: string
  description: string
  tag?: string | null
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-full w-full flex-col items-start gap-1 rounded-xl bg-white p-4 text-left ring-1 ring-ink-200 transition-shadow hover:ring-ink-400 disabled:opacity-50"
    >
      <span className="flex w-full items-baseline gap-2">
        <span className="text-sm font-semibold text-ink-900">{title}</span>
        {tag ? <span className="ml-auto shrink-0 text-[11px] text-ink-500">{tag}</span> : null}
      </span>
      <span className="text-xs text-ink-600">{description}</span>
    </button>
  )
}

type SaveState = { status: 'idle' | 'saving' | 'saved' | 'failed'; at?: Date; message?: string }

function QuoteDraftEditor({
  quoteId,
  job,
  onClose,
}: {
  quoteId: string
  job: Extract<ProposalBuilderProps, { kind: 'quote' }>['job']
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const quote = useQuery(trpc.quotes.byId.queryOptions({ id: quoteId }))
  const letterhead = useLetterhead()
  const save = useMutation(trpc.quotes.update.mutationOptions())

  const [draft, setDraft] = useState<ProposalDraft | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle' })
  const [templating, setTemplating] = useState(false)

  const latest = useRef<ProposalDraft | null>(null)
  const dirty = useRef(false)
  const timer = useRef<number | null>(null)
  const queue = useRef<Promise<unknown>>(Promise.resolve())

  // Seeded once. A refetch after a save must not overwrite what is being typed.
  useEffect(() => {
    if (!quote.data || draft !== null) return
    const seeded = draftFromQuote(quote.data)

    // A price list being made a proposal: its lines stay exactly as they are,
    // as the fixed line items, and the page gets its starting blocks round
    // them. Saved straight away, so Done leaves a proposal behind.
    const plain = seeded.sections.length === 0 && seeded.options.length === 0
    if (plain && quote.data.status === 'DRAFT' && can('QUOTE_EDIT')) {
      dirty.current = true
      setDraft(asProposal(seeded))
      return
    }
    setDraft(seeded)
  }, [quote.data, draft, can])

  latest.current = draft
  const editable = quote.data?.status === 'DRAFT' && can('QUOTE_EDIT')

  const flush = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
    if (!dirty.current || !latest.current) return queue.current
    dirty.current = false
    const input = quoteInput(latest.current)

    // One save at a time and in order: the server replaces the lines on each
    // write, so a slow early save landing last would undo a later one.
    queue.current = queue.current
      .then(async () => {
        setSaveState({ status: 'saving' })
        await save.mutateAsync({ id: quoteId, ...input })
        setSaveState({ status: 'saved', at: new Date() })
      })
      .catch((error: unknown) => {
        dirty.current = true
        setSaveState({ status: 'failed', message: errorMessage(error) })
      })
    return queue.current
  }

  useEffect(() => {
    if (!dirty.current) return
    timer.current = window.setTimeout(() => void flush(), AUTOSAVE_MS)
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flush reads refs
  }, [draft])

  const update: DraftUpdate = (change) => {
    if (!editable) return
    dirty.current = true
    setDraft((current) => (current ? change(current) : current))
  }

  const close = () => {
    void flush().finally(() => {
      void queryClient.invalidateQueries(trpc.quotes.pathFilter())
      void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    })
    onClose()
  }

  if (quote.error) {
    return (
      <>
        <BuilderHeader title="Proposal" onClose={onClose} />
        <div className="p-5">
          <ErrorState message={errorMessage(quote.error)} onRetry={() => void quote.refetch()} />
        </div>
      </>
    )
  }

  if (!quote.data || !draft) {
    return (
      <>
        <BuilderHeader title="Proposal" onClose={onClose} />
        <div className="p-5">
          <SkeletonRows rows={5} />
        </div>
      </>
    )
  }

  const data = quote.data
  const site = data.job.site?.address
  const meta: ProposalMeta = {
    number: data.number,
    version: data.version,
    customerName: data.job.client.name,
    siteAddress: site ? addressLine(site) : null,
    jobNumber: formatJobNumber(data.job),
    date: data.createdAt,
  }

  return (
    <>
      <BuilderHeader
        title={`Proposal ${data.number}`}
        subtitle={`${job.client.name} · ${meta.jobNumber}`}
        onClose={close}
        status={editable ? <SaveIndicator state={saveState} /> : <span className="text-xs text-ink-500">Sent — read only</span>}
        actions={
          <>
            {editable && can('PROPOSAL_TEMPLATE_MANAGE') ? (
              <Button size="sm" variant="ghost" onClick={() => setTemplating((value) => !value)}>
                Save as template
              </Button>
            ) : null}
            <Button
              size="sm"
              variant={previewing ? 'primary' : 'secondary'}
              icon={<Icon name="eye" className="size-3.5" />}
              onClick={() => setPreviewing((value) => !value)}
            >
              {previewing ? 'Back to editing' : 'Preview as the customer'}
            </Button>
            <Button size="sm" variant="primary" onClick={close}>
              Done
            </Button>
          </>
        }
      />

      {templating ? (
        <SaveAsTemplate
          quoteId={quoteId}
          defaultName={draft.title}
          beforeSave={flush}
          onDone={() => setTemplating(false)}
        />
      ) : null}

      {!editable ? (
        <p className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-900">
          This proposal has gone to the customer, so it cannot change under them. Revise it from the billing tab
          to write a new version.
        </p>
      ) : null}

      {previewing || !editable ? (
        <Preview draft={draft} meta={meta} business={letterhead} />
      ) : (
        <ProposalCanvas
          draft={draft}
          update={update}
          mode="quote"
          clientId={job.clientId}
          jobId={job.id}
          business={letterhead}
          meta={meta}
          assistTarget={{ kind: 'quote', quoteId }}
          beforeAssist={flush}
        />
      )}
    </>
  )
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state.status === 'saving') return <span className="text-xs text-ink-500">Saving…</span>
  if (state.status === 'failed') {
    return (
      <span role="alert" className="text-xs text-red-600">
        Not saved — {state.message}
      </span>
    )
  }
  if (state.status === 'saved') {
    return <span className="text-xs text-ink-500">Saved {state.at ? relativeTime(state.at) : ''}</span>
  }
  return <span className="text-xs text-ink-500">Changes save as you type</span>
}

function SaveAsTemplate({
  quoteId,
  defaultName,
  beforeSave,
  onDone,
}: {
  quoteId: string
  defaultName: string
  beforeSave: () => Promise<unknown>
  onDone: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState('')
  const saveFrom = useMutation(
    trpc.proposalTemplates.saveFromQuote.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.proposalTemplates.pathFilter())
        onDone()
      },
    }),
  )

  return (
    <form
      className="flex flex-wrap items-end gap-3 border-b border-ink-200 bg-ink-50 px-5 py-3"
      onSubmit={(event) => {
        event.preventDefault()
        void beforeSave().then(() => saveFrom.mutate({ quoteId, name, description }))
      }}
    >
      <div className="w-64">
        <Field label="Template name" htmlFor="save-template-name">
          <Input id="save-template-name" value={name} required onChange={(event) => setName(event.target.value)} />
        </Field>
      </div>
      <div className="min-w-64 flex-1">
        <Field label="What it is for" htmlFor="save-template-description">
          <Input
            id="save-template-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
      <Button type="submit" variant="primary" loading={saveFrom.isPending} disabled={name.trim() === ''}>
        Save template
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        Cancel
      </Button>
      {saveFrom.error ? (
        <p role="alert" className="w-full text-xs text-red-600">
          {errorMessage(saveFrom.error)}
        </p>
      ) : null}
    </form>
  )
}

function TemplateBuilder({ templateId, onClose }: Extract<ProposalBuilderProps, { kind: 'template' }>) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const existing = useQuery({
    ...trpc.proposalTemplates.byId.queryOptions({ id: templateId ?? '' }),
    enabled: Boolean(templateId),
  })
  const taxRates = useQuery(trpc.catalog.taxRates.queryOptions())
  const categories = useQuery(trpc.jobs.categories.queryOptions())
  const letterhead = useLetterhead()

  const [draft, setDraft] = useState<ProposalDraft | null>(templateId ? null : starterDraft())
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    if (!existing.data || !taxRates.data || draft !== null) return
    const rates = new Map(taxRates.data.map((rate) => [rate.id, rate.rateBasisPoints]))
    setDraft(draftFromTemplate(existing.data.content, rates, existing.data.files))
    setName(existing.data.name)
    setDescription(existing.data.description ?? '')
    setCategoryId(existing.data.jobCategoryId)
  }, [existing.data, taxRates.data, draft])

  const upsert = useMutation(
    trpc.proposalTemplates.upsert.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.proposalTemplates.pathFilter())
        onClose()
      },
    }),
  )

  const update: DraftUpdate = (change) => setDraft((current) => (current ? change(current) : current))

  const title = templateId ? 'Edit template' : 'New proposal template'

  if (existing.error) {
    return (
      <>
        <BuilderHeader title={title} onClose={onClose} />
        <div className="p-5">
          <ErrorState message={errorMessage(existing.error)} onRetry={() => void existing.refetch()} />
        </div>
      </>
    )
  }

  if (!draft) {
    return (
      <>
        <BuilderHeader title={title} onClose={onClose} />
        <div className="p-5">
          <SkeletonRows rows={5} />
        </div>
      </>
    )
  }

  return (
    <>
      <BuilderHeader
        title={title}
        onClose={onClose}
        actions={
          <>
            <Button
              size="sm"
              variant={previewing ? 'primary' : 'secondary'}
              icon={<Icon name="eye" className="size-3.5" />}
              onClick={() => setPreviewing((value) => !value)}
            >
              {previewing ? 'Back to editing' : 'Preview'}
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={upsert.isPending}
              disabled={name.trim() === ''}
              onClick={() =>
                upsert.mutate({
                  ...(templateId ? { id: templateId } : {}),
                  name: name.trim(),
                  description,
                  jobCategoryId: categoryId,
                  content: templateContent(draft),
                })
              }
            >
              Save template
            </Button>
          </>
        }
      />

      <div className="grid gap-3 border-b border-ink-200 bg-ink-50 px-5 py-3 sm:grid-cols-[240px_1fr_220px]">
        <Field label="Template name" htmlFor="template-name" required>
          <Input id="template-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="What it is for" htmlFor="template-description">
          <Textarea
            id="template-description"
            rows={1}
            className="min-h-[var(--control-md)]"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Field label="Kind of work" htmlFor="template-category" hint="Offered first on jobs of this kind.">
          <Select
            id="template-category"
            value={categoryId ?? ''}
            onChange={(event) => setCategoryId(event.target.value || null)}
          >
            <option value="">Any job</option>
            {(categories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </Field>
        {upsert.error ? (
          <p role="alert" className="text-xs text-red-600 sm:col-span-3">
            {errorMessage(upsert.error)}
          </p>
        ) : null}
      </div>

      {previewing ? (
        <Preview draft={draft} meta={TEMPLATE_META} business={letterhead} template />
      ) : (
        <ProposalCanvas
          draft={draft}
          update={update}
          mode="template"
          clientId={null}
          jobId={null}
          business={letterhead}
          meta={TEMPLATE_META}
          assistTarget={{ kind: 'template', name }}
        />
      )}
    </>
  )
}

function BuilderHeader({
  title,
  subtitle,
  status,
  actions,
  onClose,
}: {
  title: string
  subtitle?: string
  status?: React.ReactNode
  actions?: React.ReactNode
  onClose: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-ink-200 px-5 py-3">
      <h2 id="proposal-builder-title" className="text-base font-semibold text-ink-900">
        {title}
      </h2>
      {subtitle ? <span className="text-xs text-ink-500">{subtitle}</span> : null}
      {status}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {actions}
        <ModalCloseButton onClose={onClose} />
      </div>
    </div>
  )
}

const TEMPLATE_META: ProposalMeta = {
  number: 'Q-0000',
  version: 1,
  customerName: 'The customer',
  siteAddress: 'Their property',
  jobNumber: '',
  date: new Date(),
}

/** The business's paper for the canvas and the preview; a plain name until it loads. */
function useLetterhead(): LetterheadBusiness {
  const trpc = useTRPC()
  const letterhead = useQuery({ ...trpc.settings.letterhead.queryOptions(), staleTime: 60_000 })
  return letterhead.data ?? { name: '' }
}

/** The customer's page, drawn from the draft as it stands. */
function Preview({
  draft,
  meta,
  business,
  template,
}: {
  draft: ProposalDraft
  meta: ProposalMeta
  business: LetterheadBusiness
  template?: boolean
}) {
  const [choice, setChoice] = useState<Choice>({ optionId: null, addOnIds: [] })
  const proposal = useMemo(() => draftAsView(draft, meta), [draft, meta])
  const totals = draftTotals(draft)

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-ink-100 p-4 sm:p-8">
      <p className="mx-auto mb-3 max-w-3xl text-xs text-ink-600">
        What the customer sees on their phone or computer. Choosing here changes nothing.
        {template ? ' Lines with no price are priced for each client when the template is used.' : ''}
        {' '}Default total {money(totals.chosen.totalIncTax)}.
      </p>
      <ProposalPage
        business={{
          name: business.name,
          address: business.address ?? null,
          phone: business.phone ?? null,
          email: business.email ?? null,
          website: business.website ?? null,
          taxNumber: business.taxNumber ?? null,
          logoUrl: business.logoUrl ?? null,
          brandColour: business.brandColour ?? null,
          accentColour: business.accentColour ?? null,
        }}
        proposal={proposal}
        choice={choice}
        onChoice={setChoice}
      />
    </div>
  )
}
